import { inject } from "fw";
import { handle, Store } from "fw-state";
import { once } from "helpers/once";
import {
    TransferPlannerRouterDetectionSummary,
    TransferPlannerRouterDetectionSummarySearchResult
} from "models/transfer-planner/transfer-planner-detection-summary";
import { TransferPlannerRepository } from "network/transfer-planner-repository";
import { LocalStorageCache } from "caching";
import { SearchResult } from "models/search-result";
import { StatusAggregationItem } from "models/aggregations";
import { toFilter } from "helpers/filter";

const CACHE_KEY = "transfer-planner-detection-summary-store";
const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_FILTER = {
    "metaData.isLatest": true
};

export class TransferPlannerDetectionSummaryNextPageAction {}
export class TransferPlannerDetectionSummaryPreviousPageAction {}
export class TransferPlannerDetectionSummaryEnsureAction {}
export class TransferPlannerDetectionSummaryRefreshAction {}

export class TransferPlannerDetectionSummarySetFilterAction {
    constructor(
        public filter: TransferPlannerDetectionSummaryFilter = {} as TransferPlannerDetectionSummaryFilter,
        public search: string = null
    ) {}
}

interface TransferPlannerDetectionSummaryShape {
    loaded: boolean;
    errorLoading: boolean;
    list: TransferPlannerRouterDetectionSummary[];
    statusCounts: StatusAggregationItem[];
    total: number;
    filter: TransferPlannerDetectionSummaryFilter;
    search: string;
    previousPageToken: string;
    nextPageToken: string;
    currentPreviousPageToken: string;
    currentNextPageToken: string;
    pageSize: number;
    pageNumber: number;
    updatedDate: Date;
}

type SavedTransferPlannerDetectionSummaryState<TransferPlannerDetectionSummaryFilter> = {
    filter: TransferPlannerDetectionSummaryFilter;
    search: string;
    pageNumber: number;
    pageSize: number;
    previousPageToken: string;
    nextPageToken: string;
    currentPreviousPageToken: string;
    currentNextPageToken: string;
};

@inject
export class TransferPlannerDetectionSummaryStore extends Store<TransferPlannerDetectionSummaryShape> {
    private cache: LocalStorageCache = new LocalStorageCache();

    constructor(private network: TransferPlannerRepository) {
        super();
    }

    defaultState() {
        return {
            loaded: false,
            errorLoading: false,
            list: [],
            statusCounts: [],
            total: 0,
            filter: {} as TransferPlannerDetectionSummaryFilter,
            search: null,
            previousPageToken: null,
            nextPageToken: null,
            currentPreviousPageToken: null,
            currentNextPageToken: null,
            pageSize: DEFAULT_PAGE_SIZE,
            pageNumber: DEFAULT_PAGE_NUMBER,
            updatedDate: new Date()
        };
    }

    private restoreState(): void {
        const savedState = this.cache?.get<SavedTransferPlannerDetectionSummaryState<TransferPlannerDetectionSummaryFilter>>(CACHE_KEY);
        if (savedState) {
            this.setState((state) => ({
                ...state,
                ...savedState
            }));
        }
    }

    @handle(TransferPlannerDetectionSummaryEnsureAction)
    public async handleTransferPlannerDetectionSummaryEnsureAction() {
        once(CACHE_KEY, async () => {
            this.restoreState();
        });
        await this.loadTransferPlannerResultsPageList();
    }

    @handle(TransferPlannerDetectionSummarySetFilterAction)
    public async handleTransferPlannerDetectionSummarySetFilterAction(action: TransferPlannerDetectionSummarySetFilterAction) {
        this.setState((state) => ({
            ...state,
            filter: action.filter,
            search: action.search,
            loaded: false,
            errorLoading: false,
            pageNumber: DEFAULT_PAGE_NUMBER,
            previousPageToken: null,
            nextPageToken: null,
            currentPreviousPageToken: null,
            currentNextPageToken: null
        }));

        await this.loadTransferPlannerResultsPageList();
    }

    @handle(TransferPlannerDetectionSummaryRefreshAction)
    public async refreshAction(action: TransferPlannerDetectionSummaryRefreshAction) {
        if (!this.state?.loaded) {
            return;
        }

        this.setState((state) => ({
            ...state,
            loaded: false
        }));

        await this.loadTransferPlannerResultsPageList();
    }

    @handle(TransferPlannerDetectionSummaryNextPageAction)
    public async nextPageAction(action: TransferPlannerDetectionSummaryNextPageAction) {
        const { nextPageToken, pageSize, pageNumber, total } = this.state;
        if (pageNumber * pageSize >= total) {
            return;
        }

        this.setState((state) => ({
            ...state,
            loaded: false,
            pageNumber: pageNumber + 1
        }));

        await this.loadTransferPlannerResultsPageData(null, nextPageToken);
    }

    @handle(TransferPlannerDetectionSummaryPreviousPageAction)
    public async previousPageAction(action: TransferPlannerDetectionSummaryPreviousPageAction) {
        const { previousPageToken, pageNumber } = this.state;
        if (pageNumber <= 1) {
            return;
        }

        this.setState((state) => ({
            ...state,
            loaded: false,
            pageNumber: pageNumber - 1
        }));

        await this.loadTransferPlannerResultsPageData(previousPageToken, null);
    }

    private async loadTransferPlannerResultsPageList() {
        const { currentPreviousPageToken, currentNextPageToken } = this.state;
        this.setState((state) => ({
            ...state,
            filter: {
                ...DEFAULT_FILTER,
                ...state.filter
            }
        }));
        await this.loadTransferPlannerResultsPageData(currentPreviousPageToken, currentNextPageToken);
    }

    public getAllProfilesCount(aggregations: SearchResult<TransferPlannerRouterDetectionSummarySearchResult>): number {
        const sum_profilesverified = aggregations[AggregationNames.sum_profilesverified];
        const verified = sum_profilesverified?.Value || 0;

        const sum_profilesnonminviable = aggregations[AggregationNames.sum_profilesnonminviable];
        const nonminviable = sum_profilesnonminviable?.Value || 0;

        const sum_profilesunabletoverify = aggregations[AggregationNames.sum_profilesunabletoverify];
        const unabletoverify = sum_profilesunabletoverify?.Value || 0;

        const sum_profilesmissing = aggregations[AggregationNames.sum_profilesmissing];
        const missing = sum_profilesmissing?.Value || 0;

        const sum_profilesversionmismatch = aggregations[AggregationNames.sum_profilesversionmismatch];
        const versionmismatch = sum_profilesversionmismatch?.Value || 0;

        const sum_unroutable = aggregations[AggregationNames.sum_profilesunroutable];
        const unroutable = sum_unroutable?.Value || 0;

        // NOTE: The total profiles can also be calculated using number of profiles in profileMeta
        return verified + nonminviable + unabletoverify + missing + versionmismatch + unroutable;
    }

    private async loadTransferPlannerResultsPageData(previousPageToken: string, nextPageToken: string) {
        const aggregations = [
            "sum:profilesverified",
            "sum:profilesunabletoverify",
            "sum:profilesunroutable",
            "sum:profilesorphaned",
            "sum:profilesorphandeleted",
            "sum:profilesnonminviable",
            "sum:profilesmissing",
            "sum:profilesversionmismatch",
            "sum:profilesdelivered",
            "sum:profilesdeliveryerrors"
        ].join(" ");

        const { pageSize, search } = this.state;
        let profilesAggregatedCounts = [];

        try {
            const clientModelResponse = await this.network.listDetectionSummaries(
                this.getFilter(),
                search,
                null,
                previousPageToken,
                nextPageToken,
                null,
                aggregations,
                false,
                pageSize
            );

            profilesAggregatedCounts = this.processAggregations(clientModelResponse.Aggregations);

            this.setState((state) => ({
                ...state,
                loaded: true,
                updatedDate: new Date(),
                statusCounts: profilesAggregatedCounts,
                previousPageToken: clientModelResponse.PreviousPageToken,
                nextPageToken: clientModelResponse.NextPageToken,
                currentPreviousPageToken: previousPageToken,
                currentNextPageToken: nextPageToken,
                list: clientModelResponse.Results,
                total: clientModelResponse.Total
            }));
        } catch (err) {
            this.setState((state) => ({ ...state, loaded: true, errorLoading: true }));
        }
    }

    private processAggregations(aggregations: SearchResult<TransferPlannerRouterDetectionSummarySearchResult>): StatusAggregationItem[] {
        const profilesAggregatedCounts = [];
        profilesAggregatedCounts[AggregationNames.sum_allprofiles] = { Value: this.getAllProfilesCount(aggregations) };
        profilesAggregatedCounts[AggregationNames.sum_profilesverified] = { Value: aggregations[AggregationNames.sum_profilesverified].Value };
        profilesAggregatedCounts[AggregationNames.sum_profilesunabletoverify] = { Value: aggregations[AggregationNames.sum_profilesunabletoverify].Value };
        profilesAggregatedCounts[AggregationNames.sum_profilesmissing] = { Value: aggregations[AggregationNames.sum_profilesmissing].Value };
        profilesAggregatedCounts[AggregationNames.sum_profilesversionmismatch] = { Value: aggregations[AggregationNames.sum_profilesversionmismatch].Value };
        profilesAggregatedCounts[AggregationNames.sum_profilesnonminviable] = { Value: aggregations[AggregationNames.sum_profilesnonminviable].Value };
        profilesAggregatedCounts[AggregationNames.sum_profilesdelivered] = { Value: aggregations[AggregationNames.sum_profilesdelivered].Value };
        profilesAggregatedCounts[AggregationNames.sum_profilesdeliveryerrors] = { Value: aggregations[AggregationNames.sum_profilesdeliveryerrors].Value };
        profilesAggregatedCounts[AggregationNames.sum_profilesunroutable] = { Value: aggregations[AggregationNames.sum_profilesunroutable].Value };
        profilesAggregatedCounts[AggregationNames.sum_profilesorphaned] = { Value: aggregations[AggregationNames.sum_profilesorphaned].Value };
        profilesAggregatedCounts[AggregationNames.sum_profilesorphandeleted] = { Value: aggregations[AggregationNames.sum_profilesorphandeleted].Value };

        return profilesAggregatedCounts;
    }

    public getFilter(): string {
        const { filter } = this.state;
        return toFilter(filter);
    }
}

export interface TransferPlannerDetectionSummaryFilter {
    routerId: string;
}

export enum AggregationNames {
    "sum_allprofiles" = "sum_allprofiles", // NOTE: This is not a real aggregation, it is calculated using other aggregations
    "sum_profilesverified" = "sum_profilesverified",
    "sum_profilesunabletoverify" = "sum_profilesunabletoverify",
    "sum_profilesmissing" = "sum_profilesmissing",
    "sum_profilesversionmismatch" = "sum_profilesversionmismatch",
    "sum_profilesnonminviable" = "sum_profilesnonminviable",
    "sum_profilesdelivered" = "sum_profilesdelivered",
    "sum_profilesdeliveryerrors" = "sum_profilesdeliveryerrors",
    "sum_profilesunroutable" = "sum_profilesunroutable",
    "sum_profilesorphaned" = "sum_profilesorphaned",
    "sum_profilesorphandeleted" = "sum_profilesorphandeleted"
}
